import cn from 'classnames';
import { isNil } from 'lodash-es';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SecureButton } from '../secure_button';
import type { SCSelectProps } from '../select';
import { SCSelect } from '../select';
import { SCView } from '../view';
import type { SCFormFieldProps } from './form_field';
import { SCFormField } from './form_field';
import { DefaultRequiredStringIndicator, handleField } from './helper';
import './styles.css';

/**
 * Name: `SCFormFieldSCFormSelect`
 * Copied from: `SCFormFieldSCSelect`
 * Description: Support using eone-ui/forms/form-select instead of eone-ui/select,
 *    without touching the original component being used in the propject
 * Changes:
 *    - pass useEoneFormSelect-prop to SCSelect
 *    - adapt the style of inline-prop
 * Plan: merge back if verified
 */
export type SCFormFieldSCFormSelectProps<V extends string | number = string> = SCFormFieldProps &
  SCSelectProps<V> & {
    MultiSelect?: boolean;
    inline?: boolean;
    success?: boolean; // alternative to loading-prop
    labelName?: string;
  };

export const SCFormFieldSCFormSelect = <V extends string | number = string>({
  label,
  name,
  labelName,
  required,
  disabled,
  rules,
  visible,
  defaultValue,
  MultiSelect,
  secure,
  inline,
  ...rest
}: SCFormFieldSCFormSelectProps<V>) => {
  const [isSecure, setIsSecure] = useState(secure ?? false);

  const { setValue, getValues } = useFormContext();
  const applyValue = useCallback(
    (name: string, value: string) => {
      setValue(name, value, {
        shouldDirty: true,
        shouldTouch: true,
        // shouldValidate: true, // it depends, actually
      });
    },
    [setValue],
  );

  return (
    <SCFormField
      visible={visible}
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      rules={rules}
      defaultValue={defaultValue}
      withMarginBottom
    >
      {({ field, extraProps }) => {
        const fieldProps = { ...handleField(field, { disabledRef: true }) };
        const onChange = async (newValue) => {
          await rest.onChange?.(newValue);
          fieldProps.onChange(newValue);
          if (labelName) {
            const optionItem = rest.options?.find((item) => item.value === newValue);
            const labelValue = String(optionItem?.label ?? '');
            applyValue(labelName, labelValue);
          }
        };
        // Important Note: {...labelNameProps} is being put below of {...rest} to
        // make sure another feature {...valueAsLabelSelectProps} works as well
        // Description: recover snapshot label-name from field of props.labelName
        const labelNameProps: Partial<SCFormFieldSCFormSelectProps> = {};
        if (labelName) {
          labelNameProps.useEoneLabelRender = true;
          labelNameProps.LabelRender = () => {
            const found = rest.options?.find((item) => item.value === field.value);
            return found ? found.label : getValues(labelName);
          };
        }

        const multipleWithBackwardCompat = rest.multiple || MultiSelect;
        const useSuccessForLoading = !isNil(rest.success) && !rest.success; // e.g. success=false
        const loading = rest.loading || useSuccessForLoading;
        return (
          <div
            className={cn({
              'sc-form-field-form-select': true,
              'sc-form-field-form-select-inline': inline,
            })}
          >
            <SCView flex flexRow wFull>
              <SCView flex flex1>
                <SCView wFull>
                  <SCSelect
                    useEoneFormSelect
                    {...fieldProps}
                    {...rest}
                    {...extraProps}
                    {...labelNameProps}
                    Label={label}
                    ShowRequiredIndicator={required || !!rules?.required}
                    RequiredStringIndicator={DefaultRequiredStringIndicator}
                    loading={loading}
                    multiple={multipleWithBackwardCompat}
                    secure={isSecure}
                    value={fieldProps.value}
                    onChange={onChange}
                  />
                </SCView>
              </SCView>
              {secure && <SecureButton defaultSecure onChange={setIsSecure} />}
            </SCView>
          </div>
        );
      }}
    </SCFormField>
  );
};
